import { MappedAEMProperties } from '../../../../types';
import { useSummary } from '../../../../lib/api/swrApi';
import TrainingSummaryView, {
  CoursesSummary,
  TrainingSummaryTile,
} from '../../base/TrainingSummaryView/TrainingSummaryView';
import { SummaryItemsOrder, CourseType } from '../../base/TrainingSummaryView/CourseTile/CourseTile';
import SkeletonTrainingSummaryView from '../../base/TrainingSummaryView/SkeletonTrainingSummaryView';
import ErrorInfo from '../../base/ErrorInfo/ErrorInfo';
import { isNil } from '../../../../lib/utils/isNil';

export type TrainingSummaryProps = {
  heading?: string;
  targetPageUrl?: string;
} & SummaryItemsOrder &
  MappedAEMProperties;

const statusMappings = {
  assigned: '',
  completed: 'complete',
  'in-progress': 'started',
  overdue: 'overdue',
  'not-started': 'notStarted',
};

const typeToValue = (type: CourseType, summary: CoursesSummary) => {
  switch (type) {
    case 'completed':
      return summary.totalCompleted;
    case 'in-progress':
      return summary.totalInProgress;
    case 'assigned':
      return summary.totalAssigned;
    case 'overdue':
      return summary.totalOverdue;
    case 'not-started':
      return summary.totalNotStarted;
    default:
      return undefined;
  }
};

const typeToFilterValue = (type: CourseType, targetPageUrl: string) => {
  const status = statusMappings[type];

  return status !== '' ? `${targetPageUrl}?status=${status}` : targetPageUrl;
};

const TrainingSummary = ({ heading, summaryItemsOrder, targetPageUrl }: TrainingSummaryProps) => {
  const { data, isValidating, error } = useSummary();

  if (isValidating) {
    return <SkeletonTrainingSummaryView disableHeader={!heading} />;
  }

  if (error) {
    return <ErrorInfo error={error} />;
  }

  if (isNil(data)) {
    return null;
  }

  const tiles =
    summaryItemsOrder?.map(
      (type, index): TrainingSummaryTile => ({
        id: `tile-${type}-${index}`,
        type,
        value: typeToValue(type, data),
        ...(targetPageUrl ? { link: typeToFilterValue(type, targetPageUrl) } : {}),
      }),
    ) ?? [];

  return <TrainingSummaryView heading={heading} tiles={tiles} />;
};

export default TrainingSummary;
